<template>
  <div class="vencido-page">
    <!-- Partícula decorativa top -->
    <div class="deco-ring"></div>

    <div class="vencido-card">

      <!-- Logo -->
      <div class="brand-line">
        <span class="brand-pos">POS</span><span class="brand-wave">WAVE</span>
      </div>

      <!-- Ícono de estado -->
      <div class="status-icon" :class="comprobantePendiente ? 'pending' : (esCEO ? 'ceo' : 'other')">
        <i :class="comprobantePendiente ? 'bi bi-hourglass-split' : (esCEO ? 'bi bi-credit-card-2-front' : 'bi bi-hourglass-split')"></i>
      </div>

      <!-- Título y descripción -->
      <template v-if="esCEO">

        <!-- Bloqueada por inconsistencia -->
        <template v-if="estadoBloqueo === 'bloqueada_revision'">
          <h1 class="vencido-title">Comprobante en investigación</h1>
          <p class="vencido-sub">
            Nuestro equipo detectó una inconsistencia en tu comprobante de pago.<br>
            El acceso está <strong>suspendido temporalmente</strong> mientras investigamos.<br>
            Te contactaremos a la brevedad.
          </p>
          <div class="pending-badge" style="border-color:rgba(251,191,36,.3);color:#fbbf24;background:rgba(251,191,36,.08)">
            <i class="bi bi-exclamation-triangle-fill"></i>
            En investigación — Perdomo &amp; Asociados se pondrá en contacto
          </div>
          <div class="contacto-grid" style="margin-top:20px">
            <a href="mailto:soporte@perdomoyasociados.com" class="contacto-item email">
              <i class="bi bi-envelope-fill"></i>
              <div><span class="c-label">Contacto urgente</span><span class="c-val">soporte@perdomoyasociados.com</span></div>
            </a>
          </div>
        </template>

        <!-- Bloqueada por mora -->
        <template v-else-if="estadoBloqueo === 'bloqueada_mora'">
          <h1 class="vencido-title">Acceso suspendido por mora</h1>
          <p class="vencido-sub">
            El plazo para pagar venció y tu comprobante fue rechazado.<br>
            Para reactivar el acceso debes contactar directamente a Perdomo &amp; Asociados.
          </p>
          <div class="pending-badge" style="border-color:rgba(248,113,113,.3);color:#f87171;background:rgba(248,113,113,.08)">
            <i class="bi bi-lock-fill"></i>
            Suspendido por mora — requiere gestión manual con Perdomo
          </div>
          <div class="contacto-grid" style="margin-top:20px">
            <a href="mailto:soporte@perdomoyasociados.com" class="contacto-item email">
              <i class="bi bi-envelope-fill"></i>
              <div><span class="c-label">Regularizar pago</span><span class="c-val">soporte@perdomoyasociados.com</span></div>
            </a>
            <a href="https://wa.me/50412345678" target="_blank" class="contacto-item whatsapp">
              <i class="bi bi-whatsapp"></i>
              <div><span class="c-label">WhatsApp urgente</span><span class="c-val">+504 1234-5678</span></div>
            </a>
          </div>
        </template>

        <!-- Comprobante ya enviado — en revisión normal -->
        <template v-else-if="comprobantePendiente">
          <h1 class="vencido-title">Comprobante en revisión</h1>
          <p class="vencido-sub">
            Tu comprobante fue recibido y está siendo revisado por Perdomo &amp; Asociados.<br>
            El sistema se <strong>desbloqueará automáticamente</strong> al ser aprobado.
          </p>
          <div class="pending-badge">
            <i class="bi bi-clock-history"></i>
            Pendiente de aprobación por Perdomo &amp; Asociados
          </div>
        </template>

        <!-- Sin comprobante pendiente -->
        <template v-else>
          <!-- Empresa tenía plan activo pero no pagó la mensualidad -->
          <template v-if="teniaPlan">
            <h1 class="vencido-title">Plan vencido — renovación requerida</h1>
            <p class="vencido-sub">
              Tu plan <strong>{{ nombrePlan }}</strong> venció el
              <strong>{{ fechaVencimientoLegible }}</strong>.<br>
              Carga tu comprobante de pago para que Perdomo &amp; Asociados lo apruebe
              y el acceso se restaure automáticamente.
            </p>
          </template>

          <!-- Empresa nunca tuvo plan — trial terminó -->
          <template v-else>
            <h1 class="vencido-title">Período de prueba vencido</h1>
            <p class="vencido-sub">
              Tu empresa completó los <strong>30 días gratuitos</strong> de POSWAVE.<br>
              Para seguir operando, activa un plan con Perdomo &amp; Asociados.
            </p>
          </template>

          <!-- Contador de días vencidos -->
          <div class="dias-badge" v-if="diasVencida > 0">
            <i class="bi bi-exclamation-triangle-fill"></i>
            Sistema bloqueado hace {{ diasVencida }} día{{ diasVencida !== 1 ? 's' : '' }}
          </div>
        </template>

        <!-- CTA según si tiene o no plan previo (oculto si ya hay comprobante pendiente) -->
        <template v-if="!comprobantePendiente && teniaPlan">
          <!-- Ya tiene plan → ir a Mi Cuenta a subir comprobante -->
          <div class="cta-comprobante">
            <div class="cta-icon"><i class="bi bi-receipt"></i></div>
            <div class="cta-text">
              <strong>¿Ya realizaste el pago?</strong>
              <span>Sube tu comprobante en Mi Cuenta y Perdomo &amp; Asociados aprobará el acceso automáticamente.</span>
            </div>
            <button class="btn-mi-cuenta" @click="irMiCuenta">
              <i class="bi bi-credit-card-2-front"></i>
              Ir a Plan &amp; Facturación
            </button>
          </div>
        </template>

        <template v-else-if="!comprobantePendiente">
          <!-- Trial — mostrar planes disponibles -->
          <div class="planes-section">
            <div class="planes-header">
              <i class="bi bi-grid-3x3-gap-fill"></i>
              Planes disponibles
            </div>

            <div v-if="loadingPlanes" class="planes-loading">
              <i class="bi bi-arrow-repeat spin"></i> Cargando planes…
            </div>

            <div v-else-if="planes.length === 0" class="planes-empty">
              <p>Los planes se configurarán próximamente. Contacta a Perdomo &amp; Asociados para activar tu suscripción.</p>
            </div>

            <div v-else class="planes-grid">
              <div
                v-for="plan in planes"
                :key="plan.id"
                class="plan-card"
                :class="{ 'plan-seleccionado': planSeleccionado?.id === plan.id }"
                @click="seleccionarPlan(plan)"
              >
                <div class="plan-check" v-if="planSeleccionado?.id === plan.id">
                  <i class="bi bi-check-circle-fill"></i>
                </div>
                <div class="plan-nombre">{{ plan.nombre }}</div>
                <div class="plan-precio">
                  <span class="precio-val">$ {{ formatPrecio(plan.precio) }}</span>
                  <span class="precio-periodo">/ {{ plan.periodo_tipo === 'anual' ? 'año' : 'mes' }}</span>
                </div>
                <!-- Precio en lempiras con ISV si tenemos tipo de cambio -->
                <div v-if="tipoCambio" class="plan-hnl">
                  L. {{ formatPrecio(plan.precio * tipoCambio * 1.15) }}
                  <span class="plan-hnl-note">incluye ISV 15%</span>
                </div>
                <div v-if="plan.descripcion" class="plan-desc">{{ plan.descripcion }}</div>
                <div v-if="plan.max_sucursales" class="plan-chip">
                  <i class="bi bi-building"></i>
                  Hasta {{ plan.max_sucursales }} sucursal{{ plan.max_sucursales !== 1 ? 'es' : '' }}
                </div>
              </div>
            </div>

            <!-- Panel de pago al seleccionar un plan -->
            <div v-if="planSeleccionado && !comprobanteEnviado" class="pago-wrap">
              <!-- Desglose de precio -->
              <div class="pago-detalle">
                <div class="pago-detalle-titulo">
                  <i class="bi bi-receipt"></i>
                  Resumen de pago — {{ planSeleccionado.nombre }}
                </div>
                <div class="pago-linea">
                  <span>Precio base</span>
                  <span>$ {{ formatPrecio(planSeleccionado.precio) }}</span>
                </div>
                <div class="pago-linea">
                  <span>ISV (15%)</span>
                  <span>$ {{ formatPrecio(planSeleccionado.precio * 0.15) }}</span>
                </div>
                <div class="pago-linea pago-linea-total">
                  <span>Total USD</span>
                  <span>$ {{ formatPrecio(planSeleccionado.precio * 1.15) }}</span>
                </div>
                <div v-if="tipoCambio" class="pago-linea pago-linea-hnl">
                  <span>Total HNL (TC: L. {{ formatPrecio(tipoCambio) }})</span>
                  <span>L. {{ formatPrecio(planSeleccionado.precio * 1.15 * tipoCambio) }}</span>
                </div>
                <div v-if="fechaTipoCambio" class="pago-tc-nota">
                  <i class="bi bi-calendar3"></i>
                  Tipo de cambio BCH al {{ fechaTipoCambioLegible }}
                </div>
              </div>

              <!-- Upload comprobante -->
              <div class="upload-area" @click="$refs.fileInput.click()" :class="{ 'upload-tiene-archivo': archivoComprobante }">
                <input ref="fileInput" type="file" accept=".jpg,.jpeg,.png,.pdf,.webp" @change="onFileChange" hidden />
                <i :class="archivoComprobante ? 'bi bi-file-earmark-check-fill' : 'bi bi-cloud-upload'"></i>
                <span v-if="archivoComprobante">{{ archivoComprobante.name }}</span>
                <span v-else>Adjuntá tu comprobante de depósito<br><small>JPG, PNG, PDF, WEBP — máx. 10 MB</small></span>
              </div>

              <button
                class="btn-solicitar"
                @click="subirComprobante"
                :disabled="subiendoComprobante || !archivoComprobante"
              >
                <i :class="subiendoComprobante ? 'bi bi-arrow-repeat spin' : 'bi bi-send-fill'"></i>
                {{ subiendoComprobante ? 'Enviando…' : 'Enviar comprobante a Perdomo' }}
              </button>
              <p class="solicitar-hint">Tu comprobante quedará en revisión. Perdomo &amp; Asociados activará el plan <em style="color:#c09d62">{{ planSeleccionado.nombre }}</em> al confirmar el pago.</p>
            </div>

            <!-- Confirmación enviado -->
            <div v-if="comprobanteEnviado" class="solicitud-ok">
              <i class="bi bi-check-circle-fill"></i>
              <div>
                <strong>Comprobante enviado</strong>
                <span>Perdomo &amp; Asociados revisará tu pago y activará el plan <em>{{ planSeleccionado?.nombre }}</em>. Te avisaremos cuando esté listo.</span>
              </div>
            </div>

          </div>
        </template>

        <!-- Contacto -->
        <div class="contacto-grid">
          <a href="mailto:soporte@perdomoyasociados.com" class="contacto-item email">
            <i class="bi bi-envelope-fill"></i>
            <div>
              <span class="c-label">Correo</span>
              <span class="c-val">soporte@perdomoyasociados.com</span>
            </div>
          </a>
          <a href="https://wa.me/50412345678" target="_blank" class="contacto-item whatsapp">
            <i class="bi bi-whatsapp"></i>
            <div>
              <span class="c-label">WhatsApp</span>
              <span class="c-val">+504 1234-5678</span>
            </div>
          </a>
        </div>
      </template>

      <template v-else>
        <h1 class="vencido-title">Sistema temporalmente suspendido</h1>
        <p class="vencido-sub">
          El período de servicio de tu empresa ha vencido.<br>
          Contacta a tu <strong>CEO</strong> para que renueve el plan y el sistema se desbloquee.
        </p>
        <div class="other-notice">
          <i class="bi bi-person-badge-fill"></i>
          El CEO de tu empresa debe renovar el plan para reanudar las operaciones.
        </div>
      </template>

      <!-- Descarga de datos — disponible para CEO en cualquier estado de suspensión -->
      <div v-if="esCEO" class="descarga-datos">
        <div class="descarga-info">
          <i class="bi bi-database-fill-down"></i>
          <div>
            <strong>Exportar mis datos</strong>
            <span>Descargá toda tu información en Excel (clientes, ventas, productos, compras…)</span>
          </div>
        </div>
        <button class="btn-descarga" @click="descargarDatos" :disabled="descargando">
          <i :class="descargando ? 'bi bi-arrow-repeat spin' : 'bi bi-file-earmark-zip-fill'"></i>
          {{ descargando ? 'Generando ZIP…' : 'Descargar ZIP' }}
        </button>
      </div>

      <!-- Botón cerrar sesión -->
      <button class="btn-logout" @click="cerrarSesion">
        <i class="bi bi-box-arrow-left"></i>
        Cerrar sesión
      </button>

    </div>
  </div>
</template>

<script>
import { setPageTitle } from "@/components/pageMetadata";
import { notis } from "../../services/notificaciones.js";

export default {
  name: "PlanVencido",
  data() {
    return {
      planes: [],
      loadingPlanes: false,
      descargando: false,
      planSeleccionado: null,
      tipoCambio: null,
      fechaTipoCambio: null,
      archivoComprobante: null,
      subiendoComprobante: false,
      comprobanteEnviado: false,
    };
  },
  computed: {
    esCEO() {
      return localStorage.getItem("role") === "4";
    },
    /** plan_config guardado en localStorage al login */
    empresaPlan() {
      try {
        const raw = localStorage.getItem("empresa_plan");
        return raw ? JSON.parse(raw) : null;
      } catch {
        return null;
      }
    },
    /** true si la empresa ya tenía un plan real activado (no trial) */
    teniaPlan() {
      return !!this.empresaPlan?.nombre;
    },
    /** true si el CEO ya subió comprobante y está esperando aprobación */
    comprobantePendiente() {
      return localStorage.getItem('comprobante_pendiente') === '1';
    },
    /** Estado específico de bloqueo para mostrar mensajes diferenciados */
    estadoBloqueo() {
      return localStorage.getItem('estado_bloqueo') || null;
    },
    nombrePlan() {
      return this.empresaPlan?.nombre || "";
    },
    diasVencida() {
      const fv = localStorage.getItem("fecha_vencimiento");
      if (!fv) return 0;
      const partes = String(fv).substring(0, 10).split('-').map(Number);
      const fechaBloqueo = new Date(partes[0], partes[1] - 1, partes[2] + 5);
      const diff = Date.now() - fechaBloqueo.getTime();
      return Math.max(0, Math.floor(diff / 86400000));
    },
    fechaVencimientoLegible() {
      const fv = localStorage.getItem("fecha_vencimiento");
      if (!fv) return "";
      const partes = String(fv).substring(0, 10).split('-').map(Number);
      const d = new Date(partes[0], partes[1] - 1, partes[2]);
      return d.toLocaleDateString("es-HN", { day: "2-digit", month: "long", year: "numeric" });
    },
    fechaTipoCambioLegible() {
      if (!this.fechaTipoCambio) return '';
      const partes = String(this.fechaTipoCambio).substring(0, 10).split('-').map(Number);
      const d = new Date(partes[0], partes[1] - 1, partes[2]);
      return d.toLocaleDateString("es-HN", { day: "2-digit", month: "long", year: "numeric" });
    },
  },
  async mounted() {
    setPageTitle("Plan vencido — POSWAVE");
    if (!localStorage.getItem("auth")) {
      this.$router.replace("/login");
      return;
    }
    if (this.esCEO) {
      await this.cargarPlanes();
    }
  },
  methods: {
    async cargarPlanes() {
      this.loadingPlanes = true;
      try {
        const { getApi } = await import('../../config/getApiUrl.js');
        const token = localStorage.getItem("auth");

        // Cargar planes y tipo de cambio en paralelo
        const fechaVenc = localStorage.getItem("fecha_vencimiento")?.substring(0, 10) || '';
        const tcUrl = fechaVenc
          ? `${getApi()}/cuenta/tipo-cambio?fecha=${fechaVenc}`
          : `${getApi()}/cuenta/tipo-cambio`;

        const [resPlanes, resTc] = await Promise.all([
          fetch(`${getApi()}/empresa/planes-disponibles`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(tcUrl),
        ]);

        if (resPlanes.ok) this.planes = await resPlanes.json();
        if (resTc.ok) {
          const tc = await resTc.json();
          this.tipoCambio = tc.compra ?? tc.venta ?? null;
          // Mostramos la fecha de vencimiento (no la del BCH que puede ser día hábil anterior)
          this.fechaTipoCambio = fechaVenc || tc.fecha || null;
        }
      } catch {
        /* silencioso — mostrará estado vacío */
      } finally {
        this.loadingPlanes = false;
      }
    },
    formatPrecio(valor) {
      if (valor == null) return "—";
      return Number(valor).toLocaleString("es-HN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    seleccionarPlan(plan) {
      if (this.comprobanteEnviado) return;
      this.planSeleccionado = this.planSeleccionado?.id === plan.id ? null : plan;
      this.archivoComprobante = null;
    },
    onFileChange(e) {
      this.archivoComprobante = e.target.files?.[0] ?? null;
    },
    async subirComprobante() {
      if (!this.planSeleccionado || !this.archivoComprobante || this.subiendoComprobante) return;
      this.subiendoComprobante = true;
      try {
        const { getApi } = await import('../../config/getApiUrl.js');
        const token = localStorage.getItem('auth');

        // Calcular monto total en lempiras
        const monto = this.tipoCambio
          ? (this.planSeleccionado.precio * 1.15 * this.tipoCambio).toFixed(2)
          : (this.planSeleccionado.precio * 1.15).toFixed(2);
        const ahora = new Date();
        const periodo_mes  = ahora.getMonth() + 1;
        const periodo_anio = ahora.getFullYear();

        // 1. Subir comprobante
        const fd = new FormData();
        fd.append('comprobante', this.archivoComprobante);
        fd.append('monto', monto);
        fd.append('periodo_mes', periodo_mes);
        fd.append('periodo_anio', periodo_anio);

        const resPago = await fetch(`${getApi()}/cuenta/pago`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        const bodyPago = await resPago.json().catch(() => ({}));
        if (!resPago.ok) {
          notis('error', bodyPago.message || 'Error al subir el comprobante. Intentá de nuevo.');
          return;
        }

        // 2. Registrar solicitud de cambio de plan (no bloqueante)
        await fetch(`${getApi()}/cuenta/cambio-plan`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ id_plan_nuevo: this.planSeleccionado.id }),
        }).catch(() => {});

        // 3. Marcar estado local y redirigir al dashboard
        localStorage.setItem('comprobante_pendiente', '1');
        this.comprobanteEnviado = true;
        notis('success', 'Comprobante enviado. Accediendo al sistema…');
        // Breve pausa para que el usuario vea el mensaje, luego redirige
        const role = localStorage.getItem('role');
        setTimeout(() => {
          this.$router.replace(role === '3' ? '/ventas' : '/home');
        }, 2000);
      } catch {
        notis('error', 'Error de conexión. Verificá tu internet e intentá de nuevo.');
      } finally {
        this.subiendoComprobante = false;
      }
    },
    irMiCuenta() {
      this.$router.push("/mi-cuenta");
    },
    cerrarSesion() {
      const darkPref = localStorage.getItem("isDarkMode");
      localStorage.clear();
      if (darkPref !== null) localStorage.setItem("isDarkMode", darkPref);
      this.$router.replace("/login");
    },
    limpiarEstadoBloqueo() {
      localStorage.removeItem('estado_bloqueo');
    },
    async descargarDatos() {
      if (this.descargando) return;
      this.descargando = true;
      try {
        const { getApi } = await import('../../config/getApiUrl.js');
        const token = localStorage.getItem('auth');
        const res = await fetch(`${getApi()}/empresa/exportar-datos`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          notis('error', body.error || 'Error al generar el archivo. Intentá de nuevo.');
          return;
        }
        // Obtener nombre del archivo desde el header
        const cd = res.headers.get('Content-Disposition') || '';
        const match = cd.match(/filename="?([^"]+)"?/);
        const filename = match?.[1] ? decodeURIComponent(match[1]) : 'POSWAVE_datos.zip';

        const blob = await res.blob();
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch {
        notis('error', 'Error de conexión. Verificá tu internet e intentá de nuevo.');
      } finally {
        this.descargando = false;
      }
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800&display=swap");

* {
  font-family: "Montserrat", sans-serif;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

/* ── Layout ─────────────────────────────────────────── */
.vencido-page {
  min-height: 100vh;
  background: radial-gradient(ellipse at 20% 0%, rgba(192, 157, 98, 0.06) 0%, transparent 55%),
    #0d0d0d;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  position: relative;
  overflow: hidden;
}

.deco-ring {
  position: absolute;
  top: -160px;
  right: -160px;
  width: 480px;
  height: 480px;
  border-radius: 50%;
  border: 1px solid rgba(192, 157, 98, 0.08);
  pointer-events: none;
}

.vencido-card {
  width: 100%;
  max-width: 560px;
  background: #161616;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 20px;
  padding: 44px 40px 36px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  animation: fadeUp 0.4s ease;
}

@keyframes fadeUp {
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── Marca ──────────────────────────────────────────── */
.brand-line {
  display: flex;
  align-items: baseline;
  line-height: 1;
}
.brand-pos  { font-size: 1.6rem; font-weight: 800; color: #c09d62; letter-spacing: -0.02em; }
.brand-wave { font-size: 1.6rem; font-weight: 300; color: rgba(255, 255, 255, 0.55); letter-spacing: -0.02em; }

/* ── Ícono estado ───────────────────────────────────── */
.status-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
}
.status-icon.ceo     { background: rgba(239, 68, 68, 0.12);  color: #f87171; border: 1.5px solid rgba(239,68,68,0.25); }
.status-icon.other   { background: rgba(234, 179, 8, 0.1);   color: #fbbf24; border: 1.5px solid rgba(234,179,8,0.22); }
.status-icon.pending { background: rgba(192, 157, 98, 0.12); color: #c09d62; border: 1.5px solid rgba(192,157,98,0.3); }

/* ── Títulos ────────────────────────────────────────── */
.vencido-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: #f1f5f9;
  text-align: center;
  text-wrap: balance;
}

.vencido-sub {
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.45);
  text-align: center;
  line-height: 1.7;
  max-width: 380px;
}
.vencido-sub strong { color: rgba(255, 255, 255, 0.7); }

/* ── Badge días vencida ─────────────────────────────── */
.dias-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.22);
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #f87171;
  width: 100%;
}
.dias-badge i { flex-shrink: 0; }

/* ── Badge pendiente ────────────────────────────────── */
.pending-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(192, 157, 98, 0.08);
  border: 1px solid rgba(192, 157, 98, 0.3);
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #c09d62;
  width: 100%;
}
.pending-badge i { flex-shrink: 0; }

/* ── CTA comprobante (empresa con plan activo) ──────── */
.cta-comprobante {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  background: rgba(192, 157, 98, 0.06);
  border: 1.5px solid rgba(192, 157, 98, 0.25);
  border-radius: 14px;
  padding: 22px 24px;
  text-align: center;
}

.cta-icon {
  font-size: 2rem;
  color: #c09d62;
}

.cta-text {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.cta-text strong {
  font-size: 0.88rem;
  color: #f1f5f9;
  font-weight: 700;
}

.cta-text span {
  font-size: 0.76rem;
  color: rgba(255,255,255,0.45);
  line-height: 1.6;
  max-width: 360px;
}

.btn-mi-cuenta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 24px;
  background: #c09d62;
  color: #0d0d0d;
  border: none;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 700;
  font-family: "Montserrat", sans-serif;
  cursor: pointer;
  transition: background 0.2s, transform 0.15s;
  margin-top: 4px;
}

.btn-mi-cuenta:hover {
  background: #d4b07a;
  transform: translateY(-1px);
}

/* ── Sección planes ─────────────────────────────────── */
.planes-section {
  width: 100%;
  border: 1.5px solid rgba(192, 157, 98, 0.2);
  border-radius: 12px;
  padding: 20px;
}

.planes-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  font-weight: 700;
  color: #c09d62;
  margin-bottom: 14px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.planes-loading {
  text-align: center;
  font-size: 0.78rem;
  color: rgba(255,255,255,0.35);
  padding: 12px 0;
}

.planes-empty {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.35);
  line-height: 1.6;
  text-align: center;
}

/* Grid de planes */
.planes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
}

.plan-card {
  background: #1e1e1e;
  border: 1.5px solid rgba(255,255,255,0.07);
  border-radius: 10px;
  padding: 16px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  position: relative;
  transition: border-color 0.2s, background 0.2s, transform 0.15s;
  cursor: pointer;
}

.plan-card:hover {
  border-color: rgba(192, 157, 98, 0.35);
  transform: translateY(-2px);
}

.plan-card.plan-seleccionado {
  border-color: #c09d62;
  background: rgba(192, 157, 98, 0.07);
}

.plan-check {
  position: absolute;
  top: 10px;
  right: 10px;
  color: #c09d62;
  font-size: 1rem;
}

/* ── Precio HNL en card ─────────────────────────────── */
.plan-hnl {
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(255,255,255,0.5);
  margin-top: 2px;
}
.plan-hnl-note {
  font-size: 0.62rem;
  font-weight: 400;
  color: rgba(255,255,255,0.3);
  margin-left: 4px;
}

/* ── Panel de pago ──────────────────────────────────── */
.pago-wrap {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.pago-detalle {
  background: rgba(192, 157, 98, 0.05);
  border: 1px solid rgba(192, 157, 98, 0.2);
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pago-detalle-titulo {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 0.75rem;
  font-weight: 700;
  color: #c09d62;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
}

.pago-linea {
  display: flex;
  justify-content: space-between;
  font-size: 0.78rem;
  color: rgba(255,255,255,0.5);
}

.pago-linea-total {
  color: rgba(255,255,255,0.8);
  font-weight: 700;
  padding-top: 6px;
  border-top: 1px solid rgba(255,255,255,0.08);
}

.pago-linea-hnl {
  color: #c09d62;
  font-weight: 700;
  font-size: 0.82rem;
}

.pago-tc-nota {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.67rem;
  color: rgba(255,255,255,0.25);
  margin-top: 2px;
}

/* ── Upload comprobante ─────────────────────────────── */
.upload-area {
  border: 1.5px dashed rgba(192, 157, 98, 0.3);
  border-radius: 10px;
  padding: 18px 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  text-align: center;
}

.upload-area:hover {
  border-color: rgba(192, 157, 98, 0.6);
  background: rgba(192, 157, 98, 0.04);
}

.upload-area.upload-tiene-archivo {
  border-color: rgba(74, 222, 128, 0.5);
  background: rgba(74, 222, 128, 0.04);
}

.upload-area i {
  font-size: 1.8rem;
  color: #c09d62;
}

.upload-tiene-archivo i {
  color: #4ade80;
}

.upload-area span {
  font-size: 0.75rem;
  color: rgba(255,255,255,0.45);
  line-height: 1.5;
}

.upload-area small {
  font-size: 0.65rem;
  color: rgba(255,255,255,0.25);
}

/* ── Solicitar plan ─────────────────────────────────── */
.solicitar-wrap {
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.btn-solicitar {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 28px;
  background: #c09d62;
  color: #0d0d0d;
  border: none;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 700;
  font-family: "Montserrat", sans-serif;
  cursor: pointer;
  transition: background 0.2s, transform 0.15s;
  width: 100%;
  justify-content: center;
}

.btn-solicitar:hover:not(:disabled) {
  background: #d4b07a;
  transform: translateY(-1px);
}

.btn-solicitar:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.solicitar-hint {
  font-size: 0.7rem;
  color: rgba(255,255,255,0.3);
  text-align: center;
  line-height: 1.5;
}

.solicitud-ok {
  margin-top: 14px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: rgba(74, 222, 128, 0.07);
  border: 1px solid rgba(74, 222, 128, 0.25);
  border-radius: 10px;
  padding: 14px 16px;
  width: 100%;
}

.solicitud-ok > i {
  color: #4ade80;
  font-size: 1.2rem;
  flex-shrink: 0;
  margin-top: 2px;
}

.solicitud-ok > div {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.solicitud-ok strong {
  font-size: 0.85rem;
  color: #f1f5f9;
  font-weight: 700;
}

.solicitud-ok span {
  font-size: 0.72rem;
  color: rgba(255,255,255,0.45);
  line-height: 1.5;
}

.solicitud-ok em {
  font-style: normal;
  color: #c09d62;
}

.plan-nombre {
  font-size: 0.85rem;
  font-weight: 700;
  color: #f1f5f9;
}

.plan-precio {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.precio-val {
  font-size: 1rem;
  font-weight: 800;
  color: #c09d62;
}

.precio-periodo {
  font-size: 0.68rem;
  color: rgba(255,255,255,0.35);
}

.plan-desc {
  font-size: 0.72rem;
  color: rgba(255,255,255,0.4);
  line-height: 1.5;
  margin-top: 2px;
}

.plan-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.65rem;
  font-weight: 600;
  color: rgba(255,255,255,0.45);
  background: rgba(255,255,255,0.05);
  border-radius: 4px;
  padding: 3px 8px;
  width: fit-content;
  margin-top: 2px;
}

.plan-badge-actual {
  font-size: 0.62rem;
  font-weight: 700;
  color: #c09d62;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-top: 4px;
}

/* Spinner */
.spin {
  display: inline-block;
  animation: spin 1s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Contacto grid ──────────────────────────────────── */
.contacto-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  width: 100%;
}

.contacto-item {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #1e1e1e;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 10px;
  padding: 14px 16px;
  text-decoration: none;
  transition: border-color 0.2s, transform 0.15s;
}
.contacto-item:hover { border-color: rgba(192, 157, 98, 0.4); transform: translateY(-2px); }
.contacto-item i     { font-size: 1.3rem; flex-shrink: 0; }
.contacto-item.email     i { color: #60a5fa; }
.contacto-item.whatsapp  i { color: #4ade80; }

.c-label {
  display: block;
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.3);
  margin-bottom: 3px;
}
.c-val {
  display: block;
  font-size: 0.73rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.75);
}

/* ── Aviso para otros roles ─────────────────────────── */
.other-notice {
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(234, 179, 8, 0.07);
  border: 1px solid rgba(234, 179, 8, 0.18);
  border-radius: 10px;
  padding: 16px 20px;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.5;
  width: 100%;
}
.other-notice i { font-size: 1.4rem; color: #fbbf24; flex-shrink: 0; }

/* ── Descarga de datos ──────────────────────────────── */
.descarga-datos {
  width: 100%;
  border: 1.5px solid rgba(96, 165, 250, 0.2);
  border-radius: 14px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: rgba(96, 165, 250, 0.04);
}

.descarga-info {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.descarga-info > i {
  font-size: 1.4rem;
  color: #60a5fa;
  flex-shrink: 0;
  margin-top: 2px;
}

.descarga-info > div {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.descarga-info strong {
  font-size: 0.85rem;
  color: #f1f5f9;
  font-weight: 700;
}

.descarga-info span {
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.38);
  line-height: 1.5;
}

.btn-descarga {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 22px;
  background: rgba(96, 165, 250, 0.12);
  border: 1px solid rgba(96, 165, 250, 0.35);
  border-radius: 10px;
  color: #60a5fa;
  font-size: 0.82rem;
  font-weight: 700;
  font-family: "Montserrat", sans-serif;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s, transform 0.15s;
  width: 100%;
}

.btn-descarga:hover:not(:disabled) {
  background: rgba(96, 165, 250, 0.2);
  border-color: rgba(96, 165, 250, 0.6);
  transform: translateY(-1px);
}

.btn-descarga:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ── Botón logout ───────────────────────────────────── */
.btn-logout {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  padding: 11px 28px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.8rem;
  font-weight: 600;
  font-family: "Montserrat", sans-serif;
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s;
}
.btn-logout:hover { border-color: rgba(239, 68, 68, 0.5); color: #f87171; }

/* ── Responsive ─────────────────────────────────────── */
@media (max-width: 520px) {
  .vencido-card      { padding: 32px 20px 28px; }
  .contacto-grid     { grid-template-columns: 1fr; }
  .vencido-title     { font-size: 1.15rem; }
  .planes-grid       { grid-template-columns: 1fr; }
}
</style>
